import { Box, Button, Flex, Text } from '@chakra-ui/react';
import {
  JobSearchQuery,
  JobState,
  jobRecordsApi,
} from '@texas/api/endpoints/jobRecordsApi';
import { Role } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchFilterInput } from '../SearchFilterInput';
import { BodyOverride } from '../shared/BodyOverride';
import { RoleRestrict } from '../shared/RoleRestrict';
import { DataTableContainer } from '../shared/dataTable/DataTableContainer';
import { EntityDirectoryPage } from '../shared/entityDirectory/EntityDirectoryPage';
import { EntityDirectorySelectFilter } from '../shared/entityDirectory/EntityDirectorySelectFilter';
import { TexasSwitch } from '../shared/form/TexasSwitch';
import { jobsColumns } from './Columns';
import { getJobStateLabel, getJobTypeLabel } from './jobPresentation';

const pageSize = 10;
const DEFAULT_QUERY: JobSearchQuery = {
  searchTerm: '',
  state: null,
  type: null,
  page: 1,
  pageSize,
  days: 30,
  showAll: false,
};

export function Jobs() {
  const { t } = useTranslation();
  const { data, refetch, loading, error } = useApiResource(
    jobRecordsApi.search,
  );
  const [query, setQuery] = useState<JobSearchQuery>(DEFAULT_QUERY);

  useEffect(() => {
    refetch(query);
  }, [query, refetch]);

  const summary = data?.summary;
  const totalItems = data?.totalItems ?? 0;
  const firstItem = totalItems === 0 ? 0 : (query.page - 1) * pageSize + 1;
  const lastItem = Math.min(query.page * pageSize, totalItems);
  const columns = useMemo(() => jobsColumns(t), [t]);
  const stateOptions = useMemo(
    () =>
      Object.values(JobState)
        .filter((value): value is JobState => typeof value === 'number')
        .map((state) => ({
          label: getJobStateLabel(state, t),
          value: state,
        })),
    [t],
  );
  const typeOptions = useMemo(
    () =>
      (data?.types ?? []).map((type) => ({
        label: getJobTypeLabel(type, t),
        value: type,
      })),
    [data?.types, t],
  );
  const dayOptions = useMemo(
    () =>
      [7, 30, 90].map((days) => ({
        label: t('jobs.lastDays', { count: days }),
        value: days,
      })),
    [t],
  );

  return (
    <BodyOverride>
      <EntityDirectoryPage
        actions={
          <Button
            variant="entityDirectorySecondary"
            onClick={() => refetch(query)}
          >
            {t('jobs.refresh')}
          </Button>
        }
        description={t('jobs.description')}
        title={t('jobs.title')}
      >
        <Flex
          bg="entityDirectory.surface"
          border="1px solid"
          borderColor="entityDirectory.border"
        >
          <Summary
            value={summary?.running ?? 0}
            label={t('jobs.runningNow')}
            accent="#c6502c"
          />
          <Summary value={summary?.waiting ?? 0} label={t('jobs.waiting')} />
          <Summary
            value={summary?.needsAttention ?? 0}
            label={t('jobs.needsAttention')}
            accent="#9a3428"
          />
          <Summary
            value={summary?.completed ?? 0}
            label={t('jobs.completedInRange')}
            last={true}
          />
        </Flex>

        <Box
          bg="entityDirectory.surface"
          border="1px solid"
          borderColor="entityDirectory.border"
          minH={{ base: 'auto', lg: '752px' }}
        >
          <Flex
            align={{ base: 'stretch', lg: 'center' }}
            borderBottom="1px solid"
            borderColor="entityDirectory.border"
            direction={{ base: 'column', lg: 'row' }}
            flexWrap="wrap"
            gap={2.5}
            minH="94px"
            px="22px"
            py={5}
          >
            <Box flex="1 1 280px" maxW={{ base: 'full', lg: '356px' }}>
              <SearchFilterInput
                placeholder={t('jobs.searchPlaceholder')}
                style="entityDirectory"
                value={query.searchTerm}
                onChange={(searchTerm) =>
                  setQuery({
                    ...query,
                    searchTerm,
                    page: 1,
                  })
                }
              />
            </Box>
            <EntityDirectorySelectFilter
              buttonProps={{
                h: '42px',
                justifyContent: 'space-between',
                w: { base: 'full', sm: '166px' },
              }}
              options={stateOptions}
              placeholder={t('jobs.allStatuses')}
              value={query.state}
              onChange={(state) => setQuery({ ...query, state, page: 1 })}
            />
            <EntityDirectorySelectFilter
              buttonProps={{
                h: '42px',
                justifyContent: 'space-between',
                w: { base: 'full', sm: '184px' },
              }}
              options={typeOptions}
              placeholder={t('jobs.allTypes')}
              value={query.type}
              onChange={(type) => setQuery({ ...query, type, page: 1 })}
            />
            <Text
              color="entityDirectory.mutedText"
              fontSize="13px"
              ml={{ base: 0, lg: 'auto' }}
            >
              {t('jobs.count', { count: totalItems })}
            </Text>
            <EntityDirectorySelectFilter
              buttonProps={{
                h: '42px',
                justifyContent: 'space-between',
                w: { base: 'full', sm: '156px' },
              }}
              options={dayOptions}
              placeholder={t('jobs.lastDays', { count: 30 })}
              value={query.days}
              onChange={(days) =>
                setQuery({ ...query, days: days ?? 30, page: 1 })
              }
            />
            <RoleRestrict allow={[Role.SystemAdministrator]}>
              <TexasSwitch
                checked={query.showAll}
                label={t('jobs.showAll')}
                onChange={(showAll) => setQuery({ ...query, showAll, page: 1 })}
              />
            </RoleRestrict>
          </Flex>
          <DataTableContainer
            bg="entityDirectory.surface"
            error={error}
            tableId="jobs"
            w="100%"
            datatable={{
              columns,
              data: data?.items ?? [],
              isLoading: loading,
              variant: 'entity-directory',
            }}
            footer={
              <Text color="entityDirectory.mutedText" fontSize="12px">
                {t('jobs.showingResults', {
                  first: firstItem,
                  last: lastItem,
                  total: totalItems,
                })}
              </Text>
            }
            footerVariant="entityDirectoryBordered"
            pagination={{
              totalItems,
              pageSize,
              currentPage: query.page,
              onPageChange: (page) => setQuery({ ...query, page }),
              variant: 'entityDirectoryPagination',
            }}
          />
        </Box>
      </EntityDirectoryPage>
    </BodyOverride>
  );
}

function Summary({
  value,
  label,
  accent,
  last = false,
}: {
  value: number;
  label: string;
  accent?: string;
  last?: boolean;
}) {
  return (
    <Flex
      direction="column"
      flex={1}
      gap={1}
      px={6}
      py={5}
      borderRight={last ? undefined : '1px solid'}
      borderColor="entityDirectory.border"
    >
      <Text
        color={accent ?? 'entityDirectory.text'}
        fontFamily="barlow"
        fontSize="30px"
        fontWeight="bold"
      >
        {value}
      </Text>
      <Text color="entityDirectory.mutedText" fontSize="13px">
        {label}
      </Text>
    </Flex>
  );
}
